<template>
    <div
        :id="blok.anchor"
        class="ui-advanced-title"
        :class="componentClasses"
        data-test="ui-advanced-title"
        :style="[componentStyles, borderColorSettings(blok.font?.[0])]"
    >
        <NuxtImg
            v-if="blok.image?.filename"
            :src="blok.image.filename"
            :title="blok.image.title"
            :alt="blok.image.alt"
            :style="imageStyles"
            :class="imageClasses"
            provider="storyblok"
            format="webp"
            loading="lazy"
            densities="x1"
        />
        <component
            :is="blok.headingType ? blok.headingType : 'h2'"
            :class="classes"
            :style="fontColorSettings(blok.font?.[0])"
        >
            <nuxt-link
                v-if="blok.link.url || blok.link.cached_url"
                :to="getInternalLink(blok.link)"
                :target="blok.link.target || '_self'"
                :rel="blok.rel.join(' ')"
                external
            >
                <span v-html="blok.text"></span>
            </nuxt-link>
            <span v-else v-html="blok.text"></span>
        </component>
    </div>
</template>

<script setup lang="ts">
import { EHideOnType, EImagePositions } from '@nuxt-monorepo/ui/types/enums/ComponentStyle';
import type { ICoreAdvancedTitle } from '@nuxt-monorepo/ui/types/components/CoreAdvancedTitle';
import { fontClasses, fontColorSettings, dividerSettings, borderSettings } from '@nuxt-monorepo/ui/utils/fontClasses';

const { blok } = defineProps<{ blok: ICoreAdvancedTitle }>();

const mobileBreakpoint = computed(() => blok.mobileBreakpoint || 'md');

const classes = computed(() => fontClasses(blok, mobileBreakpoint.value, false));

const componentStyles = computed(() => ({
    'justify-content':
        blok.image?.filename && blok.font?.[0]?.alignment ? flexAlignment(blok.font?.[0].alignment) : null,
    'padding-top': blok.paddingY ? `${blok.paddingY}px` : null,
    'padding-bottom': blok.paddingY ? `${blok.paddingY}px` : null,
    'padding-left': blok.paddingX ? `${blok.paddingX}px` : null,
    'padding-right': blok.paddingX ? `${blok.paddingX}px` : null,
}));

const flexAlignment = (alignment: string) => {
    return alignment === 'left' ? 'flex-start' : alignment === 'center' ? 'center' : 'flex-end';
};

const componentClasses = [
    blok.mobileFont?.[0]?.alignment ? `text-${blok.mobileFont?.[0]?.alignment}` : '',
    blok.font?.[0]?.alignment ? `${mobileBreakpoint.value}:text-${blok.font?.[0]?.alignment}` : '',
    blok.image?.filename ? 'has-image' : '',
    blok.mobileFont?.[0]?.fontSize || blok.font?.[0]?.fontSize ? '' : 'no-font-size',
    ...borderSettings(blok.mobileFont?.[0], blok.font?.[0], mobileBreakpoint.value),
    ...dividerSettings(blok.mobileFont?.[0], blok.font?.[0], mobileBreakpoint.value),
];

const imageStyles = computed(() => ({
    width: blok.imageSize ? `${blok.imageSize}px` : null,
}));

const imageClasses = computed(() => ({
    'hide-desktop': blok.imageHideOn === EHideOnType.Desktop,
    'hide-mobile': blok.imageHideOn === EHideOnType.Mobile,
    'position-left': blok.imagePosition === EImagePositions.LEFT,
    'position-right': blok.imagePosition === EImagePositions.RIGHT,
}));
</script>

<style scoped lang="postcss">
.ui-advanced-title {
    &.has-image {
        @apply flex flex-col md:flex-row items-center gap-x-4;
        .position-right {
            @apply order-2;
        }
    }

    &.no-font-size {
        h1 {
            @apply text-4xl;
        }
        h2 {
            @apply text-4xl;
        }
        h3 {
            @apply text-3xl;
        }
        h4 {
            @apply text-2xl;
        }
        h5 {
            @apply text-xl;
        }
        h6 {
            @apply text-lg;
        }

        h1,
        h2,
        h3,
        h4,
        h5,
        h6 {
            @apply leading-[normal];
        }
    }

    .hide-desktop {
        @apply md:hidden;
    }
    .hide-mobile {
        @apply max-md:hidden;
    }

    &.has-underline-border {
        @apply border-b-[1px];
    }

    &.has-divider {
        @apply relative;
        &:before {
            @apply content-[""] absolute bottom-0 left-0 h-[3px] w-full bg-[#26A9E0];
        }
    }

    @screen md {
        &.md\:has-underline-border {
            @apply border-b-[1px];
            &.has-divider {
                @apply before:hidden;
            }
        }
        &.md\:has-divider {
            @apply relative;
            &:before {
                @apply content-[""] absolute bottom-0 left-0 h-[3px] w-full bg-[#26A9E0];
            }
            &.has-underline-border {
                @apply border-b-0;
            }
        }
    }

    @screen lg {
        &.lg\:has-underline-border {
            @apply border-b-[1px];
            &.has-divider {
                @apply before:hidden;
            }
        }
        &.lg\:has-divider {
            @apply relative;
            &:before {
                @apply content-[""] absolute bottom-0 left-0 h-[3px] w-full bg-[#26A9E0];
            }
            &.has-underline-border {
                @apply border-b-0;
            }
        }
    }

    @screen xl {
        &.xl\:has-underline-border {
            @apply border-b-[1px];
            &.has-divider {
                @apply before:hidden;
            }
        }
        &.xl\:has-divider {
            @apply relative;
            &:before {
                @apply content-[""] absolute bottom-0 left-0 h-[3px] w-full bg-[#26A9E0];
            }
            &.has-underline-border {
                @apply border-b-0;
            }
        }
    }
}
</style>
